import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { isAdmin } from "../../../utils/auth";
import { listRequests } from "../../../components/Admin/accessApi";
import { backendHealthy, saveAdminName, storedAdminName } from "../../../components/Admin/adminApi";
import { AdminShellContext, timeAgo } from "../../../components/Admin/adminShell";
import styles from "./AdminLayout.module.css";

const FIREBASE_CONSOLE = "https://console.firebase.google.com/project/mrehab-88593/overview";
const HEALTH_POLL_MS = 60_000;

/** Admin shell: guard, nav, freshness + refresh, backend status, and the admin's name for the audit trail. */
export default function AdminLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const allowed = isAdmin();
  const [refreshKey, setRefreshKey] = useState(0);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [pending, setPending] = useState<number | null>(null);
  const [healthy, setHealthy] = useState<boolean | null>(null);
  const [name, setName] = useState(storedAdminName());
  const [, tick] = useState(0);

  useEffect(() => {
    if (!allowed) navigate("/login", { replace: true });
  }, [allowed, navigate]);

  useEffect(() => {
    if (!allowed) return;
    listRequests("pending")
      .then((rows) => setPending(rows.length))
      .catch(() => setPending(null));
  }, [allowed, refreshKey]);

  useEffect(() => {
    if (!allowed) return;
    let live = true;
    const check = () => backendHealthy().then((ok) => live && setHealthy(ok));
    check();
    const id = window.setInterval(check, HEALTH_POLL_MS);
    // Re-render "Updated N ago" while the page sits open.
    const clock = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => {
      live = false;
      window.clearInterval(id);
      window.clearInterval(clock);
    };
  }, [allowed, refreshKey]);

  const reportUpdated = useCallback((iso: string) => setUpdatedAt(iso), []);
  const shell = useMemo(() => ({ refreshKey, reportUpdated }), [refreshKey, reportUpdated]);

  if (!allowed) return null;

  const editName = () => {
    const typed = window.prompt("Your name (remembered in this browser and recorded with every change you make):", name);
    if (typed !== null) setName(saveAdminName(typed));
  };

  const nav = [
    { to: "/admin", label: "Overview", active: pathname === "/admin" || pathname.startsWith("/admin/patients") },
    { to: "/admin/requests", label: "Access requests", active: pathname.startsWith("/admin/requests"), count: pending },
  ];

  return (
    <div className={styles.page}>
      <header className={styles.bar}>
        <nav className={styles.nav} aria-label="Admin">
          {nav.map((n) => (
            <Link key={n.to} to={n.to} className={n.active ? styles.active : styles.link} aria-current={n.active ? "page" : undefined}>
              {n.label}
              {n.count ? <span className="ui-badge ui-badge--warning">{n.count}</span> : null}
            </Link>
          ))}
        </nav>
        <div className={styles.tools}>
          <span className={styles.status} role="status">
            <span className={healthy === false ? styles.dotDown : healthy ? styles.dotUp : styles.dot} aria-hidden="true" />
            {healthy === false ? "Backend unreachable" : healthy ? "Backend online" : "Checking backend…"}
          </span>
          {updatedAt && <span className={styles.updated}>Updated {timeAgo(updatedAt)}</span>}
          <button type="button" className="ui-btn ui-btn--sm ui-btn--secondary" onClick={() => setRefreshKey((k) => k + 1)}>
            Refresh
          </button>
          <a className={styles.link} href={FIREBASE_CONSOLE} target="_blank" rel="noreferrer">
            Firebase console
          </a>
          <button type="button" className="ui-btn ui-btn--sm ui-btn--ghost" onClick={editName} title="Recorded with every change you make">
            {name ? `You: ${name}` : "Set your name"}
          </button>
        </div>
      </header>
      {healthy === false && (
        <p className="ui-banner ui-banner--error" role="alert">
          The mRehab backend is not responding, so the numbers below may be missing or stale.
        </p>
      )}
      <AdminShellContext.Provider value={shell}>
        <Outlet />
      </AdminShellContext.Provider>
    </div>
  );
}
